/**
 * Referrals, opened cards, interrupts, Lead controls, layers and the 0700
 * handover (DESIGN §6.5, §6.13, §6.17, §7).
 */
import { diag } from '../content/compendium.ts';
import { PRESETS } from '../content/presets.ts';
import type { PublicState, Role, TableState } from '../types.ts';
import { readLine, spend, ventsFree, waitingICU, type SeatHandler } from './act.ts';
import { emit, eventActive, freeBedIndex, hasAccess, patient, place, roleTitle, seatedRoles } from './common.ts';
import { release } from './outcomes.ts';
import type { SeatCommand } from '../commands.ts';

type H<T extends SeatCommand['type']> = SeatHandler<Extract<SeatCommand, { type: T }>>;
const ZERO = () => 0;
const ONE = () => 1;
const NO_TARGET = () => null;

function isLead(t: TableState, role: Role): string | null {
  return t.lead === role ? null : 'Only the Shift Lead can do that.';
}

function holderReason(pub: PublicState, t: TableState, role: Role, id: string): string | null {
  const p = pub.patients[id];
  if (!p || p.table !== t.no || p.status !== 'ACTIVE') return 'That patient is not on your table.';
  if (p.seat !== role) return 'Only the patient’s holder can do that.';
  return null;
}

/** Can a referral to `to` be made for this patient? (public) */
export function referReason(pub: PublicState, t: TableState, role: Role, id: string, to: Role): string | null {
  const h = holderReason(pub, t, role, id);
  if (h) return h;
  if (eventActive(pub, 'E26')) return 'Comms are down: no referrals this turn.';
  if (to === role) return 'The patient is already on your list.';
  if (!t.seats[to]) return `There is no ${roleTitle(to)} at this table.`;
  const p = pub.patients[id]!;
  if (p.colour === 'RED' && p.needsStabilise) return 'Stabilise a Red before referring.';
  if (p.needsStabilise && to === 'ICU') return 'Stabilise first.';
  if (to === 'ICU') {
    const ok = p.colour === 'RED' || waitingICU(p);
    if (!ok) return 'The ICU takes stabilised Reds and patients whose route names ICU.';
    if (freeBedIndex(pub, t, 'ICU') < 0) return 'No free ICU bed: the patient waits where they are and counts as having no bed.';
  }
  if (p.seat === 'ICU') return 'ICU patients leave by step-down.';
  return null;
}

/** E19: every referral costs its sender an action. E5 (this turn and next): the Physician's do too; the two add up. */
export function referCost(pub: PublicState, role: Role): number {
  return (eventActive(pub, 'E19') ? 1 : 0) + (role === 'PHYS' && eventActive(pub, 'E5') ? 1 : 0);
}

const REFER: H<'REFER'> = {
  phases: ['ACT'],
  cost: (pub, _t, role) => referCost(pub, role),
  target: (_pub, _t, _r, c) => c.p,
  check: (pub, t, role, c) => referReason(pub, t, role, c.p, c.to),
  run: (ctx, t, role, c) => {
    const p = patient(ctx.s, c.p);
    const cost = referCost(ctx.s, role);
    spend(ctx, t, role, cost, c.p, 'REFER');
    t.openCards = t.openCards.filter((o) => o.patient !== p.id);
    const bed = freeBedIndex(ctx.s, t, c.to);
    place(ctx.s, t, c.to, p, bed >= 0 ? bed : 'corridor');
    p.referral = { from: role, turn: ctx.s.clock.turn };
    const sender = t.seats[role]!;
    const receiver = t.seats[c.to]!;
    sender.stats.referralsOut++;
    receiver.stats.referralsIn++;
    if (bed < 0) receiver.stats.referralsToCorridor++;
    if (c.to === 'ICU' && t.board.vents && diag(p.believedKey).vent && ventsFree(ctx.s, t) > 0) p.flags.ventHeld = true;
    emit(ctx, {
      type: 'REFERRED', table: t.no, seat: c.to, patient: p.id, severity: bed < 0 ? 1 : 0, tag: bed < 0 ? 'Closed loop' : undefined,
      text: `${p.name} referred by ${roleTitle(role)} to ${roleTitle(c.to)}${bed < 0 ? ' — into the corridor (no free bed)' : ''}.`,
      audience: ['faculty', `seat:${role}`, `seat:${c.to}`], data: { from: role, to: c.to, corridor: bed < 0 },
    });
  },
};

const OPEN_CARD: H<'OPEN_CARD'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (pub, t, role, c) => {
    const h = holderReason(pub, t, role, c.p);
    if (h) return h;
    if (eventActive(pub, 'E26')) return 'Comms are down: no consults or help this turn.';
    if (c.to === role || !t.seats[c.to]) return 'Choose a teammate.';
    if (t.openCards.some((o) => o.patient === c.p && o.to === c.to)) return 'Already open to them.';
    return null;
  },
  run: (ctx, t, role, c) => {
    t.openCards.push({ patient: c.p, to: c.to, from: role });
    const plan = t.drafts[c.to];
    if (plan?.ready) {
      plan.ready = false;
      plan.revision++;
    }
    emit(ctx, { type: 'CARD_OPENED', table: t.no, seat: c.to, patient: c.p, text: `${roleTitle(role)} opened ${ctx.s.patients[c.p]!.name} to ${roleTitle(c.to)}.`, audience: ['faculty', `seat:${c.to}`] });
  },
};

const CLOSE_CARD: H<'CLOSE_CARD'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (_pub, t, role, c) => (t.openCards.some((o) => o.patient === c.p && o.to === c.to && o.from === role) ? null : 'That card is not open to them.'),
  run: (ctx, t, role, c) => {
    t.openCards = t.openCards.filter((o) => !(o.patient === c.p && o.to === c.to && o.from === role));
    const plan = t.drafts[c.to];
    if (plan && !hasAccess(ctx.s, t, c.to, patient(ctx.s, c.p))) {
      plan.actions = plan.actions.filter(a => !('p' in a.cmd) || a.cmd.p !== c.p);
      plan.ready = false;
      plan.revision++;
    }
    emit(ctx, { type: 'CARD_CLOSED', table: t.no, seat: c.to, patient: c.p, text: `${roleTitle(role)} closed the shared card; any care planned through it was removed.`, audience: ['faculty', `seat:${role}`, `seat:${c.to}`] });
  },
};

const RESOLVE_INTERRUPT: H<'RESOLVE_INTERRUPT'> = {
  phases: ['ACT'],
  cost: ONE,
  target: NO_TARGET,
  check: (pub, t, role, c) => {
    const i = t.interrupts.find((x) => x.id === c.i);
    if (!i || i.state === 'RESOLVED') return 'That interruption has already been dealt with.';
    if (i.seat !== role) return 'That interruption is for someone else.';
    if (i.bonus && c.p) {
      const p = pub.patients[c.p];
      if (!p || p.seat !== role || p.status !== 'ACTIVE') return 'Choose a patient on your list for the critical result.';
    }
    return null;
  },
  run: (ctx, t, role, c) => {
    const i = t.interrupts.find((x) => x.id === c.i)!;
    spend(ctx, t, role, 1, null, 'RESOLVE_INTERRUPT');
    i.state = 'RESOLVED';
    emit(ctx, { type: 'INTERRUPT_RESOLVED', table: t.no, seat: role, text: `${roleTitle(role)} dealt with: ${i.title}.`, audience: ['faculty', `seat:${role}`] });
    if (i.bonus && c.p) {
      const p = patient(ctx.s, c.p);
      const d = diag(p.believedKey);
      if (d.definitive) readLine(ctx, t, p, d.definitive === 'OPINION_PSYCH' && !t.fiveSeat ? 'ASSESS' : d.definitive, role);
    }
    // E44: the Lead's review reads the worried-about patient's definitive line, for their holder
    const rp = i.event === 'E44' && i.patient ? ctx.s.patients[i.patient] : undefined;
    if (rp && rp.status === 'ACTIVE' && rp.seat) {
      const d = diag(rp.believedKey);
      if (d.definitive) readLine(ctx, t, rp, d.definitive === 'OPINION_PSYCH' && !t.fiveSeat ? 'ASSESS' : d.definitive, rp.seat);
    }
  },
};

const CALL_FOR_HELP: H<'CALL_FOR_HELP'> = {
  phases: ['ACT'],
  cost: ONE,
  target: NO_TARGET,
  check: (pub, t, role) => isLead(t, role) ?? (t.callsForHelp >= PRESETS[pub.config.preset].callsForHelp ? 'No calls for help left this shift.' : null),
  run: (ctx, t, role) => {
    spend(ctx, t, role, 1, null, 'CALL_FOR_HELP');
    const turn = ctx.s.clock.turn;
    const from = turn + 1;
    const booking = t.bookings.length;
    t.bookings.push({ kind: 'LOCUM', bookedTurn: turn, from, until: null, used: 0, request: 'CRISIS' });
    t.callsForHelp++;
    t.dashboard.calls++;
    t.tableStats.callTurns.push(turn);
    t.locums.push({ id: `L${t.locums.length + 1}`, arrivesTurn: from, untilTurn: null, actionsLeft: 2, booking });
    emit(ctx, { type: 'CALL_FOR_HELP', table: t.no, seat: role, severity: 1, tag: 'Calling for help early', text: `Table ${t.no} called for help: a Locum arrives in turn ${from}.`, audience: ['faculty', 'table'] });
  },
};

const BOOK: H<'BOOK'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (pub, t, role, c) => {
    if (c.kind === 'THEATRE') return isLead(t, role) ?? (t.bookings.some((b) => b.kind === 'THEATRE') ? 'The second Theatre has already been booked this shift.' : null);
    if (c.kind === 'BED_MEETING') return isLead(t, role) ?? (t.bookings.some((b) => b.kind === 'BED_MEETING') ? 'The bed meeting has already been booked this shift.' : null);
    return isLead(t, role) ?? (t.callsForHelp >= PRESETS[pub.config.preset].callsForHelp ? 'No calls for help left this shift.' : null);
  },
  run: (ctx, t, role, c) => {
    const turn = ctx.s.clock.turn;
    if (c.kind === 'THEATRE' || c.kind === 'BED_MEETING') {
      t.bookings.push({ kind: c.kind, bookedTurn: turn, from: turn + 2, until: turn + 3, used: 0, request: 'PLANNED' });
      const resource = c.kind === 'THEATRE' ? 'a second Theatre with an anaesthetist' : 'a bed meeting: first ward discharge free';
      emit(ctx, { type: 'RESOURCE_BOOKED', table: t.no, seat: role, severity: 1, tag: 'Planning', text: `Table ${t.no} booked ${resource} for turns ${turn + 2}–${turn + 3}.`, audience: ['faculty', 'table'] });
      return;
    }
    const booking = t.bookings.length;
    t.bookings.push({ kind: 'LOCUM', bookedTurn: turn, from: turn + 1, until: null, used: 0, request: 'PLANNED' });
    t.callsForHelp++;
    t.dashboard.calls++;
    t.tableStats.callTurns.push(turn);
    t.locums.push({ id: `L${t.locums.length + 1}`, arrivesTurn: turn + 1, untilTurn: null, actionsLeft: 2, booking });
    emit(ctx, { type: 'RESOURCE_BOOKED', table: t.no, seat: role, severity: 1, tag: 'Planning', text: `Table ${t.no} booked a Locum for turn ${turn + 1}.`, audience: ['faculty', 'table'] });
  },
};

const TIME_OUT: H<'TIME_OUT'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (_pub, t, role) => isLead(t, role) ?? (t.timeOut.used ? 'The Time-Out has been used.' : null),
  run: (ctx, t, role) => {
    t.timeOut = { used: true, turn: ctx.s.clock.turn };
    emit(ctx, { type: 'TIME_OUT', table: t.no, seat: role, severity: 1, tag: 'Calling for help early', text: `Table ${t.no} called a Time-Out: nobody acts, no waiting time passes, everyone breathes.`, audience: ['faculty', 'table'] });
  },
};

const ALLOCATE: H<'ALLOCATE'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (_pub, t, role, c) => isLead(t, role) ?? (c.to && !t.seats[c.to] ? 'Nobody in that seat.' : null),
  run: (_ctx, t, _role, c) => {
    if (c.slot === 'theatre') t.board.allocations.theatre = c.to;
    else t.board.allocations.scanner[c.slot === 'scanner0' ? 0 : 1] = c.to;
  },
};

export function divertAllowed(pub: PublicState, t: TableState): string | null {
  if (!PRESETS[pub.config.preset].divert) return 'There is no diversion in this preset.';
  if (pub.tables.length < 3) return 'Diversion needs three or more tables in the room.';
  return null;
}

const SET_DIVERT: H<'SET_DIVERT'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (pub, t, role, c) => {
    const l = isLead(t, role);
    if (l) return l;
    const d = divertAllowed(pub, t);
    if (d) return d;
    if (c.on && (t.overflow.EM?.ratio ?? 0) <= 0) return 'Emergency must have more patients than beds to go on divert.';
    if (c.on === t.board.divert) return c.on ? 'Already on divert.' : 'Not on divert.';
    return null;
  },
  run: (ctx, t, role, c) => {
    t.board.divert = c.on;
    emit(ctx, { type: c.on ? 'DIVERT_ON' : 'DIVERT_OFF', table: t.no, seat: role, severity: 2, tag: 'System thinking', text: `Table ${t.no} ${c.on ? 'went on divert' : 'came off divert'}.`, audience: ['faculty', 'table'] });
  },
};

const EVENT_CHOICE: H<'EVENT_CHOICE'> = {
  phases: ['ACT'],
  cost: (_pub, _t, _role, c) => c.choice === 'CHALLENGE' ? 1 : 0,
  target: NO_TARGET,
  check: (_pub, t, role) => isLead(t, role) ?? (t.prompts.some((q) => q.kind === 'E17') ? null : 'There is no decree to answer.'),
  run: (ctx, t, role, c) => {
    t.prompts = t.prompts.filter((q) => q.kind !== 'E17');
    applyE17(ctx.s, t, c.choice);
    if (c.choice === 'CHALLENGE') spend(ctx, t, role, 1, null, 'EVENT_CHOICE');
    emit(ctx, { type: c.choice === 'COMPLY' ? 'E17_COMPLY' : 'E17_CHALLENGE', table: t.no, seat: role, severity: 2, tag: 'Authority gradient', text: `Table ${t.no} ${c.choice === 'COMPLY' ? 'complied with' : 'challenged'} the Consultant’s decree.`, audience: ['faculty', 'table'] });
  },
};

/** Apply the E17 decision: Comply marks every Glowing patient for Exploratory Polishing. */
export function applyE17(s: PublicState, t: TableState, choice: 'COMPLY' | 'CHALLENGE'): void {
  t.effects.decree = choice;
  if (choice === 'COMPLY') {
    for (const p of Object.values(s.patients)) {
      if (p.table === t.no && p.status === 'ACTIVE' && (p.believedKey === 'mild-glowing' || p.believedKey === 'radiant-fever')) p.flags.decree = true;
    }
  }
}

const PASS_LEAD: H<'PASS_LEAD'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (_pub, t, role, c) => isLead(t, role) ?? (!t.prompts.some((q) => q.kind === 'E23') ? 'The Lead is not being called away.' : c.to === role || !t.seats[c.to] ? 'Choose a teammate.' : null),
  run: (ctx, t, role, c) => {
    t.prompts = t.prompts.filter((q) => q.kind !== 'E23');
    passLead(ctx.s, t, role, c.to);
    emit(ctx, { type: 'LEAD_PASSED', table: t.no, seat: c.to, severity: 1, tag: 'Leadership handover', text: `${roleTitle(role)} handed the Lead to ${roleTitle(c.to)}.`, audience: ['faculty', 'table'] });
  },
};

export function passLead(s: PublicState, t: TableState, from: Role, to: Role): void {
  t.lead = to;
  t.effects.leadPenalty = { role: from, turn: s.clock.turn + 1 };
}

const ASSIGN_COVER: H<'ASSIGN_COVER'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (_pub, t, role, c) => (role !== 'PHYS' ? 'The Physician chooses their cover.' : !t.prompts.some((q) => q.kind === 'E24') ? 'No MET call to cover.' : c.to === 'PHYS' || !t.seats[c.to] ? 'Choose a teammate.' : null),
  run: (ctx, t, _role, c) => {
    t.prompts = t.prompts.filter((q) => q.kind !== 'E24');
    t.effects.cover = c.to;
    emit(ctx, { type: 'COVER', table: t.no, seat: c.to, severity: 1, tag: 'Cover; handover', text: `${roleTitle(c.to)} covers the Physician’s list during the MET call.`, audience: ['faculty', 'table'] });
  },
};

const MARK_DISPUTED: H<'MARK_DISPUTED'> = {
  phases: ['ARRIVALS', 'HUDDLE', 'ACT', 'TICK_OPEN', 'LOG'],
  cost: ZERO,
  target: NO_TARGET,
  check: (_pub, t, role) => isLead(t, role),
  run: (ctx, t, role, c) => {
    t.disputes.push({ turn: ctx.s.clock.turn, note: c.note });
    emit(ctx, { type: 'DISPUTED', table: t.no, seat: role, severity: 1, tag: 'Leadership', text: `Lead marked a dispute: ${c.note || '(no note)'}`, audience: ['faculty'] });
  },
};

const LEND_STAFF: H<'LEND_STAFF'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (pub, t, role, c) => {
    if (!pub.config.layers.staff) return 'The Staff layer is off.';
    if (c.to === role || !t.seats[c.to]) return 'Choose a teammate.';
    const borrowed = t.board.staffLoans.filter(l => l.to === role && l.fromTurn <= pub.clock.turn).reduce((n, l) => n + l.n, 0);
    const promised = t.board.staffLoans.filter(l => l.from === role && l.fromTurn > pub.clock.turn).reduce((n, l) => n + l.n, 0);
    if (t.seats[role]!.staff - borrowed - promised < c.n) return 'Those Staff are borrowed or already promised.';
    return null;
  },
  run: (ctx, t, role, c) => {
    t.board.staffLoans.push({ from: role, to: c.to, n: c.n, fromTurn: ctx.s.clock.turn + 1, untilTurn: ctx.s.clock.turn + 1 });
    emit(ctx, { type: 'STAFF_LENT', table: t.no, seat: role, text: `${roleTitle(role)} lent ${c.n} Staff to ${roleTitle(c.to)} for next turn.`, audience: ['faculty', `seat:${role}`, `seat:${c.to}`] });
  },
};

const PIN_STAFF: H<'PIN_STAFF'> = {
  phases: ['ACT'],
  cost: ZERO,
  target: NO_TARGET,
  check: (pub, t, role, c) => (!pub.config.layers.staff ? 'The Staff layer is off.' : holderReason(pub, t, role, c.p)),
  run: (ctx, _t, _role, c) => {
    ctx.s.patients[c.p]!.flags.pinnedStaff = c.pinned;
  },
};

const ORDER_BLOOD: H<'ORDER_BLOOD'> = {
  phases: ['ACT'],
  cost: ONE,
  target: NO_TARGET,
  check: (pub, t) => (!t.board.blood ? 'The Blood layer is off.' : t.board.blood.orders.length > 0 ? 'An order is already on its way.' : (void pub, null)),
  run: (ctx, t, role) => {
    spend(ctx, t, role, 1, null, 'ORDER_BLOOD');
    const delay = bloodDelay(ctx.s);
    t.board.blood!.orders.push({ arriveTurn: ctx.s.clock.turn + delay, units: 4 });
    emit(ctx, { type: 'BLOOD_ORDERED', table: t.no, seat: role, text: `Four units of blood ordered; due in turn ${ctx.s.clock.turn + delay}.`, audience: ['faculty', 'table'] });
  },
};

export function bloodDelay(s: PublicState): number {
  return s.room.activeEvents.some((e) => e.key === 'E40') ? 3 : 2;
}

/** Activate one-turn staff loans at the release boundary. */
export function releaseStaffLoans(ctx: import('./common.ts').Ctx): void {
  for (const t of ctx.s.tables) {
    const available = Object.fromEntries(seatedRoles(t).map(role => [role, t.seats[role]!.staff]));
    for (const loan of t.board.staffLoans.filter(l => l.fromTurn === ctx.s.clock.turn)) {
      const n = Math.min(loan.n, Math.max(0, available[loan.from] ?? 0));
      if (n !== loan.n) emit(ctx, { type: 'STAFF_LOAN_REDUCED', table: t.no, seat: loan.from, severity: 1, text: `Only ${n} of ${loan.n} promised Staff remain available for ${loan.to}.`, audience: ['faculty', `seat:${loan.from}`, `seat:${loan.to}`] });
      loan.n = n;
      available[loan.from] = (available[loan.from] ?? 0) - n;
      t.seats[loan.from]!.staff -= n;
      t.seats[loan.to]!.staff += n;
    }
    t.board.staffLoans = t.board.staffLoans.filter(l => l.n > 0);
  }
}

export const TEAM_HANDLERS = {
  REFER, OPEN_CARD, CLOSE_CARD, RESOLVE_INTERRUPT, CALL_FOR_HELP, BOOK, TIME_OUT, ALLOCATE, SET_DIVERT, EVENT_CHOICE, PASS_LEAD,
  ASSIGN_COVER, MARK_DISPUTED, LEND_STAFF, PIN_STAFF, ORDER_BLOOD,
};

